import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';

/**
 * Live snapshot of a domain transfer order — pulls the order row, the
 * matching invoice, and every provisioning_job row linked via order_id.
 * Composed by `TransferStatusPage` to render the customer-facing timeline.
 *
 * Polls every `pollMs` (default 60s) while the browser tab is visible
 * (per BRD §FR-7 + locked decision G3-Q7). Pauses immediately on tab
 * hide; resumes on tab visible. Skips polling once the order reaches
 * a terminal state (`complete`, `failed_register_post_payment`).
 */

const POLL_MS_DEFAULT = 60_000;
const TERMINAL_STATUSES = new Set([
  'complete',
  'failed_register_post_payment',
  'failed',
  'cancelled',
]);

export interface TransferOrderRow {
  id: string;
  customer_id: string;
  domain_name: string;
  step: string;
  status: string;
  invoice_id: string | null;
  registrant_payload: Record<string, unknown> | null;
  last_error: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TransferOrderInvoice {
  id: string;
  number: string;
  total: number;
  lifecycle_state: string;
  status: string;
  issue_date: string;
  due_date: string;
}

export interface TransferOrderJob {
  id: string;
  step_kind: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  priority: number;
  payload: Record<string, unknown> | null;
  scheduled_for: string | null;
  started_at: string | null;
  finished_at: string | null;
  last_response_payload: Record<string, unknown> | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface TransferOrderSnapshot {
  order: TransferOrderRow;
  invoice: TransferOrderInvoice | null;
  jobs: TransferOrderJob[];
  /** UTC ISO when this snapshot was loaded — drives "as of" display. */
  fetchedAt: string;
}

interface State {
  data: TransferOrderSnapshot | null;
  loading: boolean;
  error: string | null;
}

export function useTransferOrder(orderId: string | undefined, pollMs = POLL_MS_DEFAULT) {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchingRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!orderId || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data: order, error: orderErr } = await supabase
        .from('domain_registration_order')
        .select(
          'id, customer_id, domain_name, step, status, invoice_id, registrant_payload, last_error, completed_at, created_at, updated_at',
        )
        .eq('id', orderId)
        .maybeSingle();
      if (orderErr) throw new Error(orderErr.message);
      if (!order) {
        setState({ data: null, loading: false, error: 'Transfer order not found.' });
        return;
      }

      const [invoiceRes, jobsRes] = await Promise.all([
        order.invoice_id
          ? supabase
              .from('invoice')
              .select('id, number, total, lifecycle_state, status, issue_date, due_date')
              .eq('id', order.invoice_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        supabase
          .from('provisioning_job')
          .select(
            'id, step_kind, status, attempt_count, max_attempts, priority, payload, scheduled_for, started_at, finished_at, last_response_payload, error_code, error_message, created_at',
          )
          .eq('order_id', orderId)
          .order('created_at', { ascending: true }),
      ]);

      if (invoiceRes.error) throw new Error(invoiceRes.error.message);
      if (jobsRes.error) throw new Error(jobsRes.error.message);

      setState({
        data: {
          order: order as TransferOrderRow,
          invoice: (invoiceRes.data as TransferOrderInvoice | null) ?? null,
          jobs: (jobsRes.data as TransferOrderJob[]) ?? [],
          fetchedAt: new Date().toISOString(),
        },
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState((prev) => ({ data: prev.data, loading: false, error: msg }));
    } finally {
      fetchingRef.current = false;
    }
  }, [orderId]);

  // Visibility-aware polling: only run setInterval while the tab is visible
  // and the order is not in a terminal state.
  useEffect(() => {
    if (!orderId) return;
    void fetchOnce();

    function clear() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    function start() {
      clear();
      intervalRef.current = setInterval(() => {
        // Stop polling once terminal — fetched the final state already.
        if (state.data && TERMINAL_STATUSES.has(state.data.order.status)) {
          clear();
          return;
        }
        void fetchOnce();
      }, pollMs);
    }

    function onVisibility() {
      if (document.visibilityState === 'visible') {
        // Refetch immediately on becoming visible so the UI is current.
        void fetchOnce();
        start();
      } else {
        clear();
      }
    }

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // We intentionally exclude state.data from deps — re-running this
    // effect every poll cycle would tear down + rebuild the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, pollMs, fetchOnce]);

  return {
    ...state,
    refetch: fetchOnce,
    isTerminal: state.data ? TERMINAL_STATUSES.has(state.data.order.status) : false,
  };
}
